import { Box, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import ApprovalMark from '~/components/Structures/ApprovalMark';
import { approvalStatusMark } from '~/components/Structures/approvalStatusMark';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { describeGenerationModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { AiGeneratedMark } from '~/viewportGrid';

/**
 * The structure set line of the dataset button: the set's approval mark,
 * which says its status to a screen reader, its name, the AI mark when a
 * model made it, then its approved and total ROI counts at the end. With
 * no set loaded it offers to load one, or, while the image has none to
 * load, to create one.
 */
export default function StructureSetLine({
    hasSetToLoad,
}: {
    /** Whether the primary image has a structure set to load. */
    hasSetToLoad: boolean;
}) {
    const intl = useIntl();
    const structureSet = useRecoilValue(currentStructureSetState);
    if (!structureSet) {
        return (
            <Typography
                component="span"
                fontSize={11}
                color="primary.main"
                noWrap
                data-testid="no-structure-set"
            >
                <FormattedMessage
                    id={
                        hasSetToLoad
                            ? 'datasetPicker.loadStructureSet'
                            : 'datasetPicker.createNewStructureSet'
                    }
                />
            </Typography>
        );
    }
    const approvedRois = structureSet.roiArray.filter(
        (roi) => roi.approvalStatus === DICOMApprovalStatus.APPROVED
    ).length;
    const generatedBy = describeGenerationModel(structureSet.generationModel);
    return (
        <Box
            display="flex"
            alignItems="center"
            gap="6px"
            flex={1}
            minWidth={0}
            data-testid="structure-set-details"
        >
            <Box
                component="span"
                role="img"
                display="flex"
                flexShrink={0}
                aria-label={intl.formatMessage({
                    id: approvalStatusMark(structureSet.approvalStatus).labelId,
                })}
                data-testid="structure-set-status"
            >
                <ApprovalMark status={structureSet.approvalStatus} size={14} />
            </Box>
            <Typography
                component="span"
                fontSize={11}
                noWrap
                minWidth={0}
                data-testid="structure-set-label"
            >
                <SensitiveInfoTypography>
                    {structureSet.structureSetLabel}
                </SensitiveInfoTypography>
            </Typography>
            {generatedBy && (
                <AiGeneratedMark
                    images={[
                        { label: undefined, pairLabel: undefined, generatedBy },
                    ]}
                    data-testid="structure-set-ai-generated"
                />
            )}
            <Box flex={1} />
            <Box component="span" flexShrink={0}>
                <FormattedMessage
                    id="datasetPicker.approvedRoiCount"
                    values={{
                        approved: (
                            <Typography variant="monoMeta">
                                {approvedRois}
                            </Typography>
                        ),
                        total: (
                            <Typography variant="monoMeta">
                                {structureSet.roiArray.length}
                            </Typography>
                        ),
                    }}
                />
            </Box>
        </Box>
    );
}
